import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { useMutation, useQuery } from '@apollo/react-hooks';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import Hidden from '@material-ui/core/Hidden';
import Drawer from '@material-ui/core/Drawer';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { Logo, SelectDropDown, AutocompleteDropdown } from 'components';
import { LOG_OUT } from 'graphql/mutations/auth';
import { UPDATE_BUSINESS_OWNER_USER } from '../../graphql/mutations/business-owner';
import { BUSINESS_OWNER } from 'graphql/queries/business-owner';
import {
  setAvailableBusinessOwners,
  setCurrentBusinessOwner
} from 'actions/businessOwner';

import { USER_TYPE } from '../../constants/base';

import useStyles from './styles';

const getAllCompanies = data => {
  return data.map((owner, index) => {
    const searchInfo = `${owner.firstName} ${owner.lastName} ${owner.companyName} ${owner.email}
    ${owner.phoneNumber} ${owner.referencePrefix} ${owner.isNotify}`;
    return {
      value: index,
      searchInfo: searchInfo,
      ...owner
    };
  });
};

function AppHeader({ className, ...props }) {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const history = useHistory();
  const { businessOwner, setBusinessOwner, setAvailableBusinessOwners } = props;
  const [value, setValue] = React.useState(null);
  const [isSuperUser, setIsSuperUser] = React.useState(false);
  const [isBO, setIsBO] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [logOut] = useMutation(LOG_OUT);
  const [updateBusinessOwner] = useMutation(UPDATE_BUSINESS_OWNER_USER);
  const { data, refetch: refetchBO } = useQuery(BUSINESS_OWNER);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const selectedLanguage = i18n.language;
  const isNotification = !!businessOwner?.selectedBusinessOwner?.isNotify;
  const [notifyValue, setNotifyValue] = React.useState(false);

  const onChangeNotif = isNewNotif => {
    const newBOData = {
      ...businessOwner.selectedBusinessOwner,
      isNotify: isNewNotif
    };

    setBusinessOwner(newBOData);
    const result = data.businessOwners.find(obj => {
      return obj.id === newBOData.id;
    });

    if (result) {
      result.isNotify = isNewNotif;
      setAvailableBusinessOwners(data.businessOwners);
    }

    delete newBOData.isSuperuser;
    delete newBOData.referencePrefix;
    delete newBOData.__typename;

    updateBusinessOwner({
      variables: { businessOwner: newBOData }
    }).then(async () => {
      await refetchBO();
      setNotifyValue(isNewNotif);
      setLoading(true);
      enqueueSnackbar(t('Business owner was successfully updated!'), {
        variant: 'success'
      });
    });
  };

  const changeLanguage = languageValue => {
    localStorage.setItem('language', languageValue);
    i18n.changeLanguage(languageValue);
  };
  const changeBusinessOwner = newValue => {
    const selectedbusinessOw = businessOwner.availableBusinessOwners[newValue];
    localStorage.setItem(
      'selectedBusinessOwner',
      JSON.stringify(selectedbusinessOw)
    );
    setValue(newValue);
    const selectedBO = businessOwner.availableBusinessOwners[newValue];
    setNotifyValue(selectedBO.isNotify);
    setBusinessOwner({
      ...selectedBO
    });
  };

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    setBusinessOwnerFromStorage(businessOwner.availableBusinessOwners);
  }, []);

  const setBusinessOwnerFromStorage = listOfBusinessOwners => {
    let selectedBusinessOwner = localStorage.getItem('selectedBusinessOwner');
    if (selectedBusinessOwner) {
      selectedBusinessOwner = JSON.parse(selectedBusinessOwner);
      const businessOwnerIndex = listOfBusinessOwners.findIndex(
        item => item.id === `${selectedBusinessOwner.id}`
      );
      if (businessOwnerIndex >= 0) {
        setValue(parseInt(businessOwnerIndex));
        const selectedBusinessOw = listOfBusinessOwners[businessOwnerIndex];
        setNotifyValue(selectedBusinessOw.isNotify);
        setBusinessOwner({ ...listOfBusinessOwners[businessOwnerIndex] });
      }
    }
  };

  React.useEffect(() => {
    if (data) {
      if (data.currentBusinessOwner.isSuperuser) {
        setBusinessOwnerFromStorage(data.businessOwners);
        setIsBO(true);
        setIsSuperUser(true);
        setAvailableBusinessOwners(data.businessOwners);
      } else {
        setIsBO(data.currentBusinessOwner.userType !== USER_TYPE.employee);
        setIsSuperUser(false);
        setBusinessOwner(data.currentBusinessOwner);
      }
    }
  }, [data]);

  return (
    <AppBar className={classNames(classes.header, className)} position="static">
      <Hidden mdUp>
        <IconButton aria-label="menu" onClick={() => setDrawerOpen(true)}>
          <MenuIcon />
        </IconButton>
      </Hidden>
      <Logo className={classes.logo} />
      <Hidden smDown>
        <nav className={classes.navigation}>
          <ul className={classes.navigationItems}>
            <MenuLink
              to="/"
              label={`${t('Overview')}`}
              activeOnlyWhenExact={true}
            />
            <MenuLink to="/bookings-list" label={`${t('Bookings')}`} />
            <MenuLink
              to="/accommodations-and-activities"
              label={`${t('Accommodations & Activities')}`}
            />
            <MenuLink to="/statistics" label={`${t('Statistics')}`} />
            <MenuLink to="/widget-settings" label={`${t('Widget')}`} />
            <MenuLink to="/settings" label={`${t('Settings')}`} />
            {data && isSuperUser && (
              <MenuLink
                to="/business-owners"
                label={`${t('Business owners')}`}
              />
            )}
            {data && isBO && <MenuLink to="/users" label={`${t('Users')}`} />}
          </ul>
        </nav>
        <div className={classes.headerActions}>
          <div className={classes.btnActionWrapper}>
            <Button className={classes.createNewBtn} onClick={handleOpenMenu}>
              <AddCircleOutlineIcon className={classes.createNewIcon} />
              <Typography className={classes.btnTitle}>
                {t('Create new')}
              </Typography>
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                selected={
                  history.location.pathname === '/accommodations/create'
                }
                classes={{ selected: classes.selectedItem }}
                onClick={() => {
                  history.push('/accommodations/create');
                }}
              >
                {t('Accommodation')}
              </MenuItem>
              <MenuItem
                selected={history.location.pathname === '/activities/create'}
                classes={{ selected: classes.selectedItem }}
                onClick={() => {
                  history.push('/activities/create');
                }}
              >
                {t('Activity')}
              </MenuItem>
            </Menu>
          </div>
          {data && !isSuperUser && (
            <div className={classes.businessOwnerNameWrapper}>
              <Typography className={classes.businessOwnerName}>
                {businessOwner.selectedBusinessOwner.firstName}{' '}
                {businessOwner.selectedBusinessOwner.lastName}
              </Typography>
            </div>
          )}
          {data && isSuperUser && (
            <div className={classes.businessOwnerSelectorWrapper}>
              <Typography className={classes.businessOwnerName}>
                {businessOwner.selectedBusinessOwner.firstName}{' '}
                {businessOwner.selectedBusinessOwner.lastName}
              </Typography>
              <AutocompleteDropdown
                value={value}
                label={'companyName'}
                search={'searchInfo'}
                onChange={changeBusinessOwner}
                items={getAllCompanies(businessOwner.availableBusinessOwners)}
              />
            </div>
          )}
          {data && (isBO || isSuperUser) && (
            <div className={classes.languageSelectorWrapper}>
              <SelectDropDown
                value={isNotification}
                onChange={onChangeNotif}
                items={[
                  { value: true, label: 'Notify ON' },
                  { value: false, label: 'Notify OFF' }
                ]}
              />
            </div>
          )}
          <div className={classes.languageSelectorWrapper}>
            <SelectDropDown
              value={selectedLanguage}
              onChange={changeLanguage}
              items={[
                { value: 'English', label: 'English' },
                { value: 'Norway', label: 'Norwegian' }
              ]}
            />
          </div>
        </div>
      </Hidden>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <div>
          <IconButton aria-label="menu" onClick={() => setDrawerOpen(false)}>
            <ArrowBackIcon />
          </IconButton>
          <nav className={classes.navigation}>
            <ul className={classes.navigationItems}>
              <MenuLink
                to="/"
                label={`${t('Overview')}`}
                activeOnlyWhenExact={true}
              />
              <MenuLink to="/bookings-list" label={`${t('Bookings')}`} />
              <MenuLink
                to="/accommodations-and-activities"
                label={`${t('Accommodations & Activities')}`}
              />
              <MenuLink to="/statistics" label={`${t('Statistics')}`} />
              <MenuLink to="/widget-settings" label={`${t('Widget')}`} />
              <MenuLink to="/settings" label={`${t('Settings')}`} />
              {data && isSuperUser && (
                <MenuLink
                  to="/business-owners"
                  label={`${t('Business owners')}`}
                />
              )}
              {data && isBO && <MenuLink to="/users" label={`${t('Users')}`} />}
            </ul>
          </nav>
          <div className={classes.headerActions}>
            <div className={classes.btnActionWrapper}>
              <Button className={classes.createNewBtn} onClick={handleOpenMenu}>
                <AddCircleOutlineIcon className={classes.createNewIcon} />
                <Typography className={classes.btnTitle}>
                  {t('Create new')}
                </Typography>
              </Button>
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  selected={
                    history.location.pathname === '/accommodations/create'
                  }
                  classes={{ selected: classes.selectedItem }}
                  onClick={() => {
                    history.push('/accommodations/create');
                  }}
                >
                  {t('Accommodation')}
                </MenuItem>
                <MenuItem
                  selected={history.location.pathname === '/activities/create'}
                  classes={{ selected: classes.selectedItem }}
                  onClick={() => {
                    history.push('/activities/create');
                  }}
                >
                  {t('Activity')}
                </MenuItem>
              </Menu>
            </div>
            {data && !isSuperUser && (
              <div className={classes.businessOwnerNameWrapper}>
                <Typography className={classes.businessOwnerName}>
                  {businessOwner.selectedBusinessOwner.firstName}{' '}
                  {businessOwner.selectedBusinessOwner.lastName}
                </Typography>
              </div>
            )}
            {data && isSuperUser && (
              <div className={classes.businessOwnerSelectorWrapper}>
                <Typography className={classes.businessOwnerName}>
                  {businessOwner.selectedBusinessOwner.firstName}{' '}
                  {businessOwner.selectedBusinessOwner.lastName}
                </Typography>
                <AutocompleteDropdown
                  value={value}
                  label={'companyName'}
                  search={'searchInfo'}
                  onChange={changeBusinessOwner}
                  items={getAllCompanies(businessOwner.availableBusinessOwners)}
                />
              </div>
            )}
            {data && (isBO || isSuperUser) && (
              <div className={classes.languageSelectorWrapper}>
                <SelectDropDown
                  value={isNotification}
                  onChange={onChangeNotif}
                  items={[
                    { value: true, label: 'Notify ON' },
                    { value: false, label: 'Notify OFF' }
                  ]}
                />
              </div>
            )}
            <div className={classes.languageSelectorWrapper}>
              <SelectDropDown
                value={selectedLanguage}
                onChange={changeLanguage}
                items={[
                  { value: 'English', label: 'English' },
                  { value: 'Norway', label: 'Norwegian' }
                ]}
              />
            </div>
          </div>
        </div>
      </Drawer>
      <div>
        <Button
          className={classes.logOutBtn}
          onClick={() =>
            logOut().then(() => {
              setBusinessOwner({});
              setAvailableBusinessOwners([]);
              history.push('/login');
            })
          }
        >
          <Typography className={classes.logOutBtnTitle}>
            {t('Log out')}
          </Typography>
        </Button>
      </div>
    </AppBar>
  );
}

function MenuLink({ label, to, activeOnlyWhenExact }) {
  const match = useRouteMatch({
    path: to,
    exact: activeOnlyWhenExact
  });

  const classes = useStyles();

  return (
    <li className={classes.navigationItem}>
      <Link
        className={classNames(classes.navigationItemLink, {
          [classes.activeNavigationLink]: match
        })}
        to={to}
      >
        {label}
      </Link>
    </li>
  );
}
const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});
const mapDispatchToProps = dispatch => ({
  setBusinessOwner: item => dispatch(setCurrentBusinessOwner(item)),
  setAvailableBusinessOwners: item => dispatch(setAvailableBusinessOwners(item))
});

export default connect(mapStateToProps, mapDispatchToProps)(AppHeader);
